import { reaction } from "mobx";
/**
 * Automatic focus management for the overlay stack.
 *
 * Restoration uses two Bootstrap events:
 * - hidden.bs.modal: fires when the last modal closes (target already in DOM).
 * - shown.bs.modal: fires after Bootstrap's focus trap and after OverlayModal
 *   re-mounts its children — needed when a parent modal re-shows after a
 *   sub-modal closes.
 *
 * On Safari, clicking a button does not focus it before the click handler runs,
 * so document.activeElement is stale at push time. We track lastClickedElement
 * instead and prefer it over activeElement for click-triggered pushes.
 */
export class OverlayFocusManager {
    constructor(stack) {
        this.stack = stack;
        this.savedFocus = new Map();
        this.previousDepth = 0;
        this.pendingRestore = null;
        this.disposer = null;
        this.lastClickedElement = null;
        this.handleClick = (e) => {
            if (e.target instanceof HTMLElement) {
                this.lastClickedElement = e.target;
            }
        };
        this.handleModalHidden = () => {
            const saved = this.pendingRestore;
            if (saved === null)
                return;
            // If we are closing a last modal from stack and we are on the basic page, we are checking if focused element is in viewport to not restore focus on element that is not visible (B2C-1357).
            const checkIsElementInViewport = this.stack.size === 0;
            if (restoreFocus(saved, checkIsElementInViewport)) {
                this.pendingRestore = null;
            }
        };
        this.handleModalShown = () => {
            const saved = this.pendingRestore;
            if (saved === null)
                return;
            this.pendingRestore = null;
            requestAnimationFrame(() => restoreFocus(saved, false));
        };
    }
    start() {
        this.previousDepth = this.stack.size;
        this.disposer = reaction(() => this.stack.size, (newDepth) => {
            var _a;
            const prevDepth = this.previousDepth;
            this.previousDepth = newDepth;
            if (newDepth > prevDepth) {
                // pop+push (replace): preserve the original saved focus instead of
                // capturing the current stale activeElement. Example: Menu -> "add to
                // cart" click -> Add to cart modal -> "add to cart" click -> Pop state
                // -> Added to Cart modal -> Pop state -> Back to Menu with focus still
                // on the clicked "Add to cart" button.
                if (this.pendingRestore) {
                    this.savedFocus.set(prevDepth, this.pendingRestore);
                    this.pendingRestore = null;
                }
                else {
                    this.savedFocus.set(prevDepth, saveActiveElement(this.lastClickedElement));
                    this.lastClickedElement = null;
                }
            }
            else if (newDepth < prevDepth) {
                this.pendingRestore = (_a = this.savedFocus.get(newDepth)) !== null && _a !== void 0 ? _a : null;
                // Clean up entries at depths we've left
                for (const d of this.savedFocus.keys()) {
                    if (d >= newDepth)
                        this.savedFocus.delete(d);
                }
            }
        });
        document.addEventListener("click", this.handleClick, { capture: true });
        document.addEventListener("hidden.bs.modal", this.handleModalHidden);
        document.addEventListener("shown.bs.modal", this.handleModalShown);
    }
    stop() {
        var _a;
        (_a = this.disposer) === null || _a === void 0 ? void 0 : _a.call(this);
        this.disposer = null;
        document.removeEventListener("click", this.handleClick, { capture: true });
        document.removeEventListener("hidden.bs.modal", this.handleModalHidden);
        document.removeEventListener("shown.bs.modal", this.handleModalShown);
        this.savedFocus.clear();
        this.pendingRestore = null;
        this.lastClickedElement = null;
    }
}
function saveActiveElement(clickTarget) {
    // On Safari, activeElement is stale at click time — use the click target and
    // walk up to the nearest focusable ancestor (handles clicks on children like
    // <i> inside <button>).
    let element = null;
    if (clickTarget === null || clickTarget === void 0 ? void 0 : clickTarget.isConnected) {
        element = clickTarget;
        while (element !== null &&
            element !== document.body &&
            element.tabIndex < 0) {
            element = element.parentElement;
        }
        if (element === document.body) {
            element = null;
        }
    }
    // Fallback to activeElement for non-click focus changes (e.g. keyboard).
    if (element === null && document.activeElement instanceof HTMLElement) {
        element = document.activeElement;
    }
    if (element === null || element === document.body) {
        return { element: null, selector: null };
    }
    return { element, selector: getElementSelector(element) };
}
function getElementSelector(el) {
    const anchor = el.closest("[id]");
    const anchorPart = (anchor === null || anchor === void 0 ? void 0 : anchor.id) ? `#${CSS.escape(anchor.id)}` : "";
    const fieldId = el.getAttribute("data-field-id");
    const fieldPart = fieldId ? `[data-field-id="${CSS.escape(fieldId)}"]` : "";
    return `${anchorPart} ${fieldPart}`.trim() || null;
}
function restoreFocus(saved, checkViewport) {
    var _a;
    // Try to focus by element reference
    if (((_a = saved.element) === null || _a === void 0 ? void 0 : _a.isConnected) &&
        tryFocusWithFallback(saved.element, checkViewport)) {
        return true;
    }
    // Selector fallback: element may be disconnected due to unmounting/re-rendering
    if (saved.selector) {
        const el = document.querySelector(saved.selector);
        if (el && tryFocusWithFallback(el, checkViewport)) {
            return true;
        }
    }
    return false;
}
// On the menu page we have dropdowns with product variants - we should focus back to the dropdown instead of the individual variant button because this variant button is invisible.
function tryFocusWithFallback(el, checkViewport) {
    if (tryFocus(el, checkViewport)) {
        return true;
    }
    const toggle = findDropdownToggle(el);
    if (toggle !== null && tryFocus(toggle, checkViewport)) {
        return true;
    }
    return false;
}
function findDropdownToggle(el) {
    var _a, _b;
    const menu = el.closest(".dropdown-menu");
    return ((_b = (_a = menu === null || menu === void 0 ? void 0 : menu.parentElement) === null || _a === void 0 ? void 0 : _a.querySelector(".dropdown-toggle")) !== null && _b !== void 0 ? _b : null);
}
// NOTICE: checkViewport: if true, skips off-screen elements to prevent
// the page jumping back to the top
function tryFocus(el, checkViewport) {
    if (checkViewport) {
        const rect = el.getBoundingClientRect();
        const isInViewport = rect.bottom > 0 &&
            rect.top < window.innerHeight &&
            rect.right > 0 &&
            rect.left < window.innerWidth;
        if (!isInViewport)
            return false;
    }
    el.focus({ preventScroll: true });
    const focused = document.activeElement === el;
    if (focused)
        el.scrollIntoView({ block: checkViewport ? "nearest" : "center" });
    return focused;
}
